import React, { useMemo, useState } from 'react';
import TransactionVolumeChart from '../../dashboard/components/TransactionVolumeChart';
import { blockExplorerUrl } from '../../../utils/wagmiConfig';
import {
    DEFAULT_DAILY_TIMELINE_DAYS,
    dailyPresetLabel,
} from '../../../utils/chartTimeline';
import { copyToClipboard } from '../../../utils/commonFunction';
import { formatDateTime } from '../formatting';
import { formatTaskStatus } from '../helpers';
import { buildDailyPayoutChartData } from '../utils/workStatsMetrics';
import {
    buildEarningsLedger,
    buildPendingPayouts,
    computeEarningsBreakdown,
} from '../utils/earningsMetrics';

const StatCard = ({ label, value, hint, accent = 'default' }) => {
    const valueClass =
        accent === 'teal'
            ? 'text-teal-700 dark:text-teal-400'
            : accent === 'amber'
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-zinc-900 dark:text-white';

    return (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
            <p className={`mt-2 text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
            {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
        </div>
    );
};

const TxLink = ({ hash }) => {
    if (!hash) return <span className="text-zinc-400">—</span>;
    const short = `${hash.slice(0, 8)}…${hash.slice(-6)}`;
    return (
        <span className="inline-flex items-center gap-1">
            <span className="font-mono text-[11px] text-zinc-600 dark:text-zinc-400" title={hash}>
                {short}
            </span>
            <button
                type="button"
                onClick={() => copyToClipboard(hash)}
                className="text-zinc-400 hover:text-teal-600 dark:hover:text-teal-400"
                title="Copy hash"
            >
                <i className="fa-regular fa-copy text-[10px]" />
            </button>
            {blockExplorerUrl ? (
                <a
                    href={`${blockExplorerUrl}/tx/${hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-400 hover:text-teal-600 dark:hover:text-teal-400"
                    title="View on explorer"
                >
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                </a>
            ) : null}
        </span>
    );
};

const JobsEarningsPage = ({
    role = 'employee',
    tasks = [],
    tokenSymbol = 'USDC',
    formatNumber,
    onOpenTask,
    onOpenHistory,
}) => {
    const isEmployee = role === 'employee';
    const [timelineDays, setTimelineDays] = useState(DEFAULT_DAILY_TIMELINE_DAYS);
    const breakdown = useMemo(
        () => computeEarningsBreakdown(tasks, role),
        [tasks, role],
    );
    const ledger = useMemo(() => buildEarningsLedger(tasks), [tasks]);
    const pending = useMemo(() => buildPendingPayouts(tasks), [tasks]);
    const chartData = useMemo(
        () => buildDailyPayoutChartData(tasks, role, timelineDays),
        [tasks, role, timelineDays],
    );
    const periodLabel = dailyPresetLabel(timelineDays);

    const formatAmt = (n) => `${formatNumber(n)} ${tokenSymbol}`;

    return (
        <div className="w-full space-y-6 pb-10">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                        {isEmployee ? 'Earnings' : 'Payouts'}
                    </h1>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                        {isEmployee
                            ? 'Money received and payouts waiting from your assignments.'
                            : 'Amounts released to your team and escrow still locked.'}
                    </p>
                </div>
                {typeof onOpenHistory === 'function' ? (
                    <button
                        type="button"
                        onClick={onOpenHistory}
                        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 shadow-sm transition hover:border-teal-300 hover:text-teal-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-teal-700 dark:hover:text-teal-400"
                    >
                        <i className="fa-solid fa-clock-rotate-left text-[10px]" />
                        View history
                    </button>
                ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <StatCard
                    label={isEmployee ? 'Total received' : 'Total paid out'}
                    value={formatAmt(breakdown.totalReleased)}
                    hint={`${breakdown.releasedCount} completed task${breakdown.releasedCount === 1 ? '' : 's'}`}
                    accent="teal"
                />
                <StatCard
                    label={isEmployee ? 'Awaiting release' : 'Awaiting approval'}
                    value={formatAmt(breakdown.pendingRelease)}
                    hint={`${breakdown.pendingCount} submitted`}
                    accent="amber"
                />
                <StatCard
                    label={isEmployee ? 'In active escrow' : 'Locked in escrow'}
                    value={formatAmt(breakdown.inEscrow)}
                    hint={
                        isEmployee
                            ? `${breakdown.activeCount} active task${breakdown.activeCount === 1 ? '' : 's'}`
                            : `${breakdown.lockedCount} task${breakdown.lockedCount === 1 ? '' : 's'} with funds held`
                    }
                />
            </div>

            <TransactionVolumeChart
                data={chartData}
                title={isEmployee ? 'Earnings trend' : 'Payout trend'}
                subtitle={`Daily ${isEmployee ? 'received' : 'released'} · ${periodLabel}`}
                secondaryLabel={isEmployee ? 'Earned' : 'Paid out'}
                trendTabLabel="Trend"
                showSentSeries={false}
                enableCompare={false}
                enableTimeline
                timelineMode="days"
                timelineDays={timelineDays}
                onTimelineChange={(days) => setTimelineDays(days)}
                valueFormat="currency"
            />

            {pending.length > 0 ? (
                <section className="rounded-2xl border border-amber-200/80 bg-amber-50/50 p-5 dark:border-amber-500/20 dark:bg-amber-500/5">
                    <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        {isEmployee ? 'Pending payout' : 'Awaiting your release'}
                    </h2>
                    <p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-300/80">
                        Submitted work not yet paid on-chain
                    </p>
                    <ul className="mt-4 divide-y divide-amber-200/60 dark:divide-amber-500/15">
                        {pending.map((row) => (
                            <li
                                key={row.id}
                                className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-zinc-900 dark:text-white">
                                        {row.title}
                                    </p>
                                    <p className="text-xs text-zinc-500">
                                        {row.date ? formatDateTime(row.date) : '—'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold tabular-nums text-amber-900 dark:text-amber-200">
                                        {formatAmt(row.amount)}
                                    </span>
                                    {typeof onOpenTask === 'function' ? (
                                        <button
                                            type="button"
                                            onClick={() => onOpenTask(row.id)}
                                            className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
                                        >
                                            Open
                                        </button>
                                    ) : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                <div className="border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                        {isEmployee ? 'Payment ledger' : 'Release ledger'}
                    </h2>
                    <p className="mt-0.5 text-xs text-zinc-500">
                        Completed tasks with amounts and transaction hashes
                    </p>
                </div>
                {ledger.length ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-zinc-100 bg-zinc-50/80 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/50">
                                    <th className="px-5 py-3">Task</th>
                                    <th className="px-5 py-3">Amount</th>
                                    <th className="px-5 py-3">Status</th>
                                    <th className="px-5 py-3">Date</th>
                                    <th className="px-5 py-3">Release tx</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                                {ledger.map((row) => (
                                    <tr key={row.id} className="hover:bg-zinc-50/80 dark:hover:bg-zinc-900/30">
                                        <td className="px-5 py-3">
                                            {typeof onOpenTask === 'function' ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onOpenTask(row.id)}
                                                    className="max-w-[200px] truncate text-left font-medium text-teal-700 hover:underline dark:text-teal-400"
                                                >
                                                    {row.title}
                                                </button>
                                            ) : (
                                                <span className="font-medium text-zinc-900 dark:text-white">
                                                    {row.title}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-semibold tabular-nums text-zinc-900 dark:text-white">
                                            {formatAmt(row.amount)}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-zinc-600 dark:text-zinc-400">
                                            {formatTaskStatus(row.status)}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-zinc-500">
                                            {row.date ? formatDateTime(row.date) : '—'}
                                        </td>
                                        <td className="px-5 py-3">
                                            <TxLink hash={row.releaseTxHash || row.fundTxHash} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="px-5 py-10 text-center text-sm text-zinc-500">
                        No completed payouts yet. Released payments will appear here.
                    </p>
                )}
            </section>
        </div>
    );
};

export default JobsEarningsPage;
